import { useEffect, useState, useRef, ChangeEvent, KeyboardEvent } from 'react';
import { InputPinWrapper } from '../styled';

interface PinInputProps {
  length?: number;
  onVerify: (code: string) => void;
}

function PinInput({ length = 4, onVerify }: PinInputProps): JSX.Element {
  const [code, setCode] = useState<string[]>([]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(
    Array(length).fill(null),
  );

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    const enteredCode = code.join('');
    onVerify(enteredCode);
  }, [code, onVerify]);

  const handleChange = (index: number, value: string) => {
    const isNumeric = /^\d*$/.test(value);
    if (isNumeric) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      const input = inputRefs.current[index];
      if (input && value && index < length - 1) {
        setTimeout(() => {
          inputRefs.current[index + 1]?.focus();
        }, 0);
      }

      const inputValueLength = input?.value.length || 0;
      input?.setSelectionRange(inputValueLength, inputValueLength);
    }
  };

  const handleBackspace = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      handleChange(index - 1, '');
    }
  };

  const handleVerify = () => {
    const isCodeFilled = code.length === length && code.every((value) => value);
    if (isCodeFilled) {
      const enteredCode = code.join('');
      onVerify(enteredCode);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const currentIndex = inputRefs.current.findIndex(
      (ref) => ref === event.target,
    );
    if (event.key === 'Enter' && code.length === length) {
      handleVerify();
    } else if (event.key === 'ArrowLeft' && currentIndex > 0) {
      inputRefs.current[currentIndex - 1]?.focus();
    } else if (event.key === 'ArrowRight' && currentIndex < length - 1) {
      inputRefs.current[currentIndex + 1]?.focus();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;
    const inputValueLength = input.value.length;
    input.setSelectionRange(inputValueLength, inputValueLength);
  };

  return (
    <InputPinWrapper data-cy="input-token-container">
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(ref) => {
            inputRefs.current[index] = ref;
          }}
          type="text"
          maxLength={1}
          value={code[index] || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleChange(index, e.target.value)
          }
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
            handleBackspace(index, e)
          }
          onKeyUp={handleKeyDown}
          onFocus={handleFocus}
          className={code[index] ? 'filled' : ''}
          data-cy="input-token"
        />
      ))}
    </InputPinWrapper>
  );
}

export default PinInput;
